import React, { useState, useMemo } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";
import {
  useGetScanPurposeBreakdownQuery,
} from "../../app/api/console/consoleCostAnalyticsApiSlice";

const COLORS = [
  "#8B5CF6", "#EC4899", "#1FAF5C", "#3B82F6", "#F59E0B",
  "#EF4444", "#06B6D4", "#84CC16", "#F97316", "#6366F1",
];

const COLUMNS = [
  { key: "expand", label: "", width: "w-[32px]" },
  { key: "timestamp", label: "Time (ET)", width: "w-[180px]" },
  { key: "scan_id", label: "Scan", width: "w-[200px]" },
  { key: "website_name", label: "Website", width: "w-[140px]" },
  { key: "env", label: "Env", width: "w-[80px]" },
  { key: "scan_mode", label: "Type", width: "w-[110px]" },
  { key: "item_count", label: "Pages / Tests", width: "w-[100px]", align: "right" },
  { key: "total_issues", label: "Findings", width: "w-[80px]", align: "right" },
  { key: "cost_usd", label: "Cost ($)", width: "w-[90px]", align: "right" },
  { key: "cost_per_item", label: "Cost / Item", width: "w-[100px]", align: "right" },
  { key: "cost_per_finding", label: "Cost / Finding", width: "w-[110px]", align: "right" },
];

const formatScanMode = (mode) => {
  if (!mode || mode === "unknown") return "—";
  return mode.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
};

const BreakdownTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className='bg-white rounded-xl border border-border-subtle shadow-lg px-3 py-2 text-body-xs'>
      <p className='font-semibold text-text-primary mb-1'>{d.name}</p>
      <div className='flex justify-between gap-3'>
        <span className='text-text-secondary'>Cost</span>
        <span className='font-semibold'>${d.cost_usd?.toFixed(4)}</span>
      </div>
      <div className='flex justify-between gap-3'>
        <span className='text-text-secondary'>Calls</span>
        <span className='font-semibold'>{d.call_count?.toLocaleString()}</span>
      </div>
    </div>
  );
};

const ExpandedRow = ({ scanId, colSpan, startDate, endDate }) => {
  const { data, isLoading } = useGetScanPurposeBreakdownQuery({ scanId, startDate, endDate });

  if (isLoading) {
    return (
      <tr>
        <td colSpan={colSpan} className='px-6 py-4 bg-surface-neutral/20'>
          <div className='flex items-center gap-2'>
            <div className='animate-spin rounded-full h-4 w-4 border-2 border-accent-purple border-t-transparent' />
            <span className='text-body-xs text-text-tertiary'>Loading purpose breakdown...</span>
          </div>
        </td>
      </tr>
    );
  }

  const byPurpose = data?.by_purpose || {};
  const items = Object.entries(byPurpose)
    .map(([name, vals]) => ({ name, ...vals }))
    .sort((a, b) => b.cost_usd - a.cost_usd);

  if (!items.length) {
    return (
      <tr>
        <td colSpan={colSpan} className='px-6 py-3 bg-surface-neutral/20 text-body-xs text-text-tertiary'>
          No purpose breakdown available
        </td>
      </tr>
    );
  }

  const chartHeight = Math.max(120, items.length * 28 + 20);

  return (
    <tr>
      <td colSpan={colSpan} className='px-4 py-3 bg-surface-neutral/20 border-b border-border-subtle'>
        <div className='flex items-start gap-6'>
          <div className='flex-1' style={{ height: chartHeight, minWidth: 0 }}>
            <ResponsiveContainer width='100%' height='100%'>
              <BarChart data={items} layout='vertical' margin={{ top: 0, right: 10, left: 0, bottom: 0 }}>
                <XAxis type='number' tick={{ fontSize: 10, fill: "rgba(10,10,38,0.4)" }} axisLine={false} tickLine={false} tickFormatter={(v) => `$${v}`} />
                <YAxis type='category' dataKey='name' width={140} tick={{ fontSize: 10, fill: "rgba(10,10,38,0.6)" }} axisLine={false} tickLine={false} />
                <Tooltip content={<BreakdownTooltip />} cursor={{ fill: "rgba(139,92,246,0.04)" }} />
                <Bar dataKey='cost_usd' radius={[0, 4, 4, 0]} barSize={16}>
                  {items.map((_, i) => (
                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className='w-[200px] flex-shrink-0 space-y-1 pt-1'>
            {items.map((item, i) => (
              <div key={item.name} className='flex items-center gap-2 text-body-xs'>
                <span className='w-2 h-2 rounded-full flex-shrink-0' style={{ backgroundColor: COLORS[i % COLORS.length] }} />
                <span className='text-text-secondary truncate'>{item.name}</span>
                <span className='font-semibold text-text-primary ml-auto'>${item.cost_usd?.toFixed(2)}</span>
              </div>
            ))}
          </div>
        </div>
      </td>
    </tr>
  );
};

const CostTable = ({ data, isLoading, error, startDate, endDate, limit }) => {
  const [sortKey, setSortKey] = useState("date");
  const [sortDir, setSortDir] = useState("desc");
  const [page, setPage] = useState(0);
  const [expandedId, setExpandedId] = useState(null);
  const perPage = 15;

  const scans = useMemo(() => data?.scans || [], [data]);

  const totals = useMemo(() => {
    let cost = 0;
    let findings = 0;
    for (const s of scans) {
      cost += s.cost_usd || 0;
      findings += s.total_issues || 0;
    }
    return { cost, findings };
  }, [scans]);

  const sorted = useMemo(() => {
    return [...scans].sort((a, b) => {
      const aVal = a[sortKey];
      const bVal = b[sortKey];
      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return 1;
      if (bVal == null) return -1;
      if (typeof aVal === "number" && typeof bVal === "number") {
        return sortDir === "asc" ? aVal - bVal : bVal - aVal;
      }
      return sortDir === "asc"
        ? String(aVal).localeCompare(String(bVal))
        : String(bVal).localeCompare(String(aVal));
    });
  }, [scans, sortKey, sortDir]);

  const paged = sorted.slice(page * perPage, (page + 1) * perPage);
  const totalPages = Math.ceil(sorted.length / perPage);

  const toggleSort = (key) => {
    if (key === "expand") return;
    if (sortKey === key) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortKey(key);
      setSortDir("desc");
    }
    setPage(0);
  };

  if (isLoading) {
    return (
      <div className='py-12 text-center'>
        <div className='animate-spin rounded-full h-6 w-6 border-2 border-accent-purple border-t-transparent mx-auto mb-2' />
        <span className='text-body-sm text-text-tertiary'>Loading scans...</span>
      </div>
    );
  }

  if (error) {
    return <p className='text-body-sm text-severity-high py-8 text-center'>Failed to load scan data</p>;
  }

  if (!scans.length) {
    return <p className='text-body-sm text-text-tertiary py-8 text-center'>No scan cost data available</p>;
  }

  return (
    <div>
      <div className='flex items-center justify-end gap-4 mb-2 text-body-xs text-text-secondary'>
        <span>
          Showing {scans.length.toLocaleString()} scans
          {limit != null ? ` (capped at ${limit.toLocaleString()})` : ""}
          {" · "}
          <span className='font-semibold text-text-primary'>${totals.cost.toFixed(2)}</span> in this table
          {totals.findings > 0 && <> · {totals.findings.toLocaleString()} findings</>}
          {limit != null && scans.length >= limit && (
            <> · use Export for the full period</>
          )}
        </span>
      </div>
      <div className='overflow-x-auto'>
        <table className='w-full text-left'>
          <thead>
            <tr className='border-b border-border-subtle'>
              {COLUMNS.map((col) => (
                <th
                  key={col.key}
                  onClick={() => toggleSort(col.key)}
                  className={`px-3 py-2.5 text-body-xs font-semibold text-text-secondary ${col.key !== "expand" ? "cursor-pointer hover:text-text-primary" : ""} transition-colors select-none ${col.width} ${col.align === "right" ? "text-right" : ""}`}
                >
                  <span className='inline-flex items-center gap-1'>
                    {col.label}
                    {sortKey === col.key && (
                      <svg className={`w-3 h-3 ${sortDir === "asc" ? "" : "rotate-180"}`} fill='none' viewBox='0 0 24 24' stroke='currentColor' strokeWidth={2}>
                        <path strokeLinecap='round' strokeLinejoin='round' d='M5 15l7-7 7 7' />
                      </svg>
                    )}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {paged.map((scan, i) => {
              const isExpanded = expandedId === scan.scan_id;
              const itemCount = scan.item_count || scan.total_pages || 0;
              const totalIssues = scan.total_issues || 0;
              const isOneLook = scan.scan_mode === "scraper" || scan.scan_mode === "scraper_one_look";

              return (
                <React.Fragment key={scan.scan_id || i}>
                  <tr
                    className={`border-b border-border-subtle/50 hover:bg-surface-neutral/30 transition-colors cursor-pointer ${isExpanded ? "bg-surface-neutral/20" : ""}`}
                    onClick={() => setExpandedId(isExpanded ? null : scan.scan_id)}
                  >
                    <td className='px-3 py-2.5 w-[32px]'>
                      <svg
                        className={`w-4 h-4 text-text-tertiary transition-transform ${isExpanded ? "rotate-90" : ""}`}
                        fill='none' viewBox='0 0 24 24' stroke='currentColor' strokeWidth={2}
                      >
                        <path strokeLinecap='round' strokeLinejoin='round' d='M9 5l7 7-7 7' />
                      </svg>
                    </td>
                    <td className='px-3 py-2.5 text-body-xs text-text-secondary' title={scan.timestamp || scan.date}>
                      {scan.timestamp
                        ? new Date(scan.timestamp).toLocaleString("en-US", { timeZone: "America/New_York", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: true }) + " ET"
                        : scan.date || "—"}
                    </td>
                    <td className='px-3 py-2.5'>
                      <span className='text-body-xs text-text-primary truncate block max-w-[200px]' title={scan.scan_id}>
                        {scan.domain || scan.website_name || scan.scan_id?.slice(0, 16) + "..."}
                      </span>
                      <div className='text-caption text-text-tertiary truncate max-w-[200px]' title={scan.scan_template_name || scan.scan_id}>
                        {scan.scan_template_name || <span className='font-mono'>{scan.scan_id?.slice(0, 12)}</span>}
                      </div>
                    </td>
                    <td className='px-3 py-2.5 text-body-xs text-text-secondary truncate max-w-[140px]' title={scan.website_name || scan.website_id}>
                      {scan.website_name || "—"}
                    </td>
                    <td className='px-3 py-2.5'>
                      <span className={`text-body-xs px-2 py-0.5 rounded-full font-medium ${
                        scan.env === "production" ? "bg-severity-high/10 text-severity-high"
                        : scan.env === "staging" ? "bg-accent-purple/10 text-accent-purple"
                        : "bg-surface-neutral text-text-secondary"
                      }`}>
                        {scan.env}
                      </span>
                    </td>
                    <td className='px-3 py-2.5 text-body-xs text-text-secondary'>
                      {formatScanMode(scan.scan_mode)}
                    </td>
                    <td className='px-3 py-2.5 text-body-xs text-text-primary text-right'>
                      {itemCount > 0 ? `${itemCount} ${isOneLook ? "pages" : "tests"}` : "—"}
                    </td>
                    <td className='px-3 py-2.5 text-body-xs text-text-primary text-right'>
                      {totalIssues > 0 ? totalIssues : "—"}
                    </td>
                    <td className='px-3 py-2.5 text-body-xs font-semibold text-text-primary text-right'>
                      ${scan.cost_usd?.toFixed(2)}
                    </td>
                    <td className='px-3 py-2.5 text-body-xs text-text-primary text-right'>
                      {scan.cost_per_item != null ? `$${scan.cost_per_item.toFixed(4)}` : "—"}
                    </td>
                    <td className='px-3 py-2.5 text-body-xs text-text-primary text-right'>
                      {scan.cost_per_finding != null ? `$${scan.cost_per_finding.toFixed(4)}` : "—"}
                    </td>
                  </tr>
                  {isExpanded && (
                    <ExpandedRow
                      scanId={scan.scan_id}
                      colSpan={COLUMNS.length}
                      startDate={startDate}
                      endDate={endDate}
                    />
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className='flex items-center justify-between mt-4 pt-3 border-t border-border-subtle'>
          <span className='text-body-xs text-text-tertiary'>
            Showing {page * perPage + 1}–{Math.min((page + 1) * perPage, sorted.length)} of {sorted.length} scans
          </span>
          <div className='flex items-center gap-1'>
            <button
              onClick={() => setPage(Math.max(0, page - 1))}
              disabled={page === 0}
              className='px-2.5 py-1 text-body-xs rounded-lg border border-border-subtle hover:bg-surface-neutral disabled:opacity-30 disabled:cursor-not-allowed transition-colors'
            >
              Prev
            </button>
            {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
              const p = page < 3 ? i : page - 2 + i;
              if (p >= totalPages) return null;
              return (
                <button
                  key={p}
                  onClick={() => setPage(p)}
                  className={`px-2.5 py-1 text-body-xs rounded-lg transition-colors ${
                    p === page ? "bg-button-primary text-white" : "border border-border-subtle hover:bg-surface-neutral"
                  }`}
                >
                  {p + 1}
                </button>
              );
            })}
            <button
              onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
              disabled={page >= totalPages - 1}
              className='px-2.5 py-1 text-body-xs rounded-lg border border-border-subtle hover:bg-surface-neutral disabled:opacity-30 disabled:cursor-not-allowed transition-colors'
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CostTable;
